import React, { useEffect, useState } from 'react';
import { Sparkles, Crown, X, ArrowRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Global upsell modal — listens for `ifaap:quota-exceeded` events emitted by
 * `api/client.js` interceptor on HTTP 402.
 *
 * Mounted once near the app root, it offers a clean upgrade path without
 * having every CTA (POST /companies, POST /sessions, ...) handle the error.
 *
 * NOTE: We deliberately use `window.location.href` for navigation rather than
 * `useNavigate`, because `AuthProvider` (where this modal is mounted) wraps
 * the React Router. Hooks like `useNavigate` would crash here.
 */
export default function UpsellModal() {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const role = (user?.role || '').toLowerCase().replace(' ', '_');
  const isSuperAdmin = role === 'super_admin' || role === 'superadmin';

  useEffect(() => {
    const onEvt = (e) => setData(e.detail || {});
    window.addEventListener('ifaap:quota-exceeded', onEvt);
    return () => window.removeEventListener('ifaap:quota-exceeded', onEvt);
  }, []);

  if (!data) return null;
  const isSuspension = data.error === 'tenant_suspended';
  const isFeatureLocked = data.error === 'feature_disabled';
  const title = isSuspension ? 'Abonnement suspendu'
              : isFeatureLocked ? `Module verrouillé : ${data.label || ''}`
              : 'Limite atteinte';
  const subtitle = isSuspension
    ? 'Régularisez votre abonnement pour réactiver toutes les fonctionnalités.'
    : isFeatureLocked
    ? `Inclus à partir du forfait Pro · Vous êtes actuellement en ${data.plan || 'Starter'}.`
    : `Forfait ${data.plan || 'Starter'} · ${data.current || 0}/${data.limit || 0} ${data.resource || ''}`;

  return (
    <div data-testid="upsell-modal" className="fixed inset-0 z-[9998] flex items-center justify-center bg-[#1A1B2E]/55 backdrop-blur-sm p-4">
      <div className="w-full max-w-[460px] rounded-2xl bg-white shadow-2xl border border-[#EFF0F6] overflow-hidden">
        <div className="bg-gradient-to-br from-[#4F46E5] to-[#5B3FE0] text-white px-6 py-5 flex items-start gap-3 relative">
          <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
            {isSuspension ? <Crown className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />}
          </div>
          <div className="flex-1 pr-7">
            <h2 className="text-[16px] font-bold mb-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              {title}
            </h2>
            <p className="text-[12.5px] opacity-90">{subtitle}</p>
          </div>
          <button
            data-testid="upsell-close"
            onClick={() => setData(null)}
            className="absolute top-4 right-4 w-7 h-7 rounded-md bg-white/15 hover:bg-white/25 flex items-center justify-center"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 py-5">
          <p className="text-[13.5px] text-[#1A1B2E] leading-relaxed mb-4">{data.message}</p>

          <div className="flex flex-col gap-2">
            {isSuperAdmin ? (
              <button
                data-testid="upsell-go-superadmin"
                onClick={() => { setData(null); window.location.href = '/superadmin'; }}
                className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center justify-center gap-1.5 shadow-sm"
              >
                Gérer mon abonnement <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                data-testid="upsell-contact"
                onClick={() => { setData(null); window.location.href = 'mailto:contact@ifaap.fr?subject=Demande%20d%27%C3%A9volution%20de%20forfait'; }}
                className="h-10 px-4 rounded-xl brand-gradient text-white text-[13px] font-semibold flex items-center justify-center gap-1.5 shadow-sm"
              >
                Contacter mon administrateur <ArrowRight className="w-4 h-4" />
              </button>
            )}
            <button
              data-testid="upsell-dismiss"
              onClick={() => setData(null)}
              className="h-10 px-4 rounded-xl bg-[#F6F7FB] text-[#4A4B63] text-[13px] font-semibold"
            >
              Plus tard
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
